import { useFetch } from "../useFetch";
import type {
	BadgeResponse,
	CreateBadgePayload,
	UpdateBadgePayload,
	BadgeAssignmentResponse,
} from "../../core/types/control";

// ============================================
// GET ALL BADGES
// ============================================
export function useBadges() {
	return useFetch<BadgeResponse[]>({
		endpoint: "/badges",
		method: "GET",
	});
}

// ============================================
// GET BADGES ASSIGNED TO A PROVIDER
// ============================================
export function useAssignedBadges(providerId: number) {
	return useFetch<BadgeAssignmentResponse[]>({
		endpoint: `/badges/${providerId}/assigned`,
		method: "GET",
		auto: providerId > 0,
	});
}

// ============================================
// CREATE BADGE
// ============================================
export function useCreateBadge() {
	const fetch = useFetch<BadgeResponse, CreateBadgePayload>({
		endpoint: "/badges",
		method: "POST",
		auto: false,
	});

	// Aceptamos data (texto) y files (imagen) por separado
	const create = (data: CreateBadgePayload, files?: { icon_badge?: File }) => {
		return fetch.execute({ body: data, files });
	};

	return { ...fetch, create };
}

// ============================================
// UPDATE BADGE
// ============================================
export function useUpdateBadge(id: number) {
	const fetch = useFetch<BadgeResponse, UpdateBadgePayload>({
		endpoint: `/badges/${id}`,
		method: "PATCH",
		auto: false,
	});

	const update = (data: UpdateBadgePayload, files?: { icon_badge?: File }) => {
		return fetch.execute({ body: data, files });
	};

	return { ...fetch, update };
}

// ============================================
// DEACTIVATE BADGE
// ============================================
export function useDeactivateBadge() {
	const fetch = useFetch<{ message: string }>({
		endpoint: "/badges",
		method: "DELETE",
		auto: false,
	});

	const deactivate = (id: number) => {
		return fetch.execute({ endpoint: `/badges/${id}` });
	};

	return { ...fetch, deactivate };
}

// ============================================
// ASSIGN BADGE TO PROVIDER
// ============================================
export function useAssignBadge() {
	// Ahora el hook sabe que recibirá la respuesta completa con el proveedor y la insignia
	const fetch = useFetch<BadgeAssignmentResponse>({
		endpoint: "/badges/assign",
		method: "POST",
		auto: false,
	});

	const assign = (providerId: number, badgeId: number) => {
		// Al ejecutar, construimos la URL dinámica
		return fetch.execute({
			endpoint: `/badges/assign/${providerId}/${badgeId}`,
		});
	};

	return { ...fetch, assign };
}

// ============================================
// DELETE BADGE ASSIGNMENT (Opcional)
// ============================================
// Si necesitas desasignar (borrar la relación), sería algo así:
export function useRevokeBadge() {
	const fetch = useFetch<{ message: string }>({
		endpoint: "/badges/assign",
		method: "DELETE",
		auto: false,
	});

	const revoke = (assignmentId: number) => {
		return fetch.execute({ endpoint: `/badges/assign/${assignmentId}` });
	};

	return { ...fetch, revoke };
}
